import { useMemo } from 'react';

import { usePartnerConfig } from '~/context/PartnerConfigContext';
import { MarketplacePartner } from '~/types/marketplace';
import { parseStepFlow } from '~/utils/premarketplace/step-flow';

export type StepIds =
  | 'introduction'
  | 'location'
  | 'categories'
  | 'household'
  | 'upgrade-profile'
  | 'financing-interest'
  | 'bundle-categories'
  | 'intent'
  | 'contact-time'
  | 'recommended-vendors'
  | 'contact';

export interface StepConfig {
  id: StepIds;
  required: boolean;
}

export interface ModalStepConfiguration {
  partner: MarketplacePartner;
  steps: StepConfig[];
}

const useOrderedSteps = () => {
  const getOrderedSteps = (steps: StepConfig[]): StepConfig[] => {
    const startingIndex = -1;
    if (startingIndex === -1) {
      return steps.filter((step) => step.required);
    }

    const startingStep = steps[startingIndex];
    const stepsBefore = steps.slice(0, startingIndex).filter((step) => step.required);
    const stepsAfter = steps.slice(startingIndex + 1).filter((step) => step.required);

    return [startingStep, ...stepsBefore, ...stepsAfter];
  };

  return { getOrderedSteps };
};

export const usePartnerStepConfiguration = (partner?: MarketplacePartner): ModalStepConfiguration | null => {
  const { getOrderedSteps } = useOrderedSteps();
  const config = usePartnerConfig();
  const steps = useMemo(() => parseStepFlow(config?.configuration?.stepFlow), [config?.configuration?.stepFlow]);

  if (!partner) return null;

  return { partner, steps: getOrderedSteps(steps) };
};
